import { useState } from 'react';
import { toast } from 'sonner';
import { useUpdateBewerber } from '../../api/bewerber';
import {
  Bewerber,
  BewerberStatus,
  OFFBOARDING_STEPS,
  OffboardingStep,
} from '../../types/bewerber';
import { StichtagInput } from './StichtagInput';

// Heroicons outline "information-circle" — matches the info icon used elsewhere in the app.
function InfoIcon({ title }: { title: string }) {
  return (
    <span className="ml-1.5 inline-flex text-gray-400 hover:text-gray-600 cursor-help" title={title}>
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span className="sr-only">{title}</span>
    </span>
  );
}

interface OffboardingChecklistProps {
  bewerber: Bewerber;
}

type StichtagField = 'offboardingLetzterArbeitstag' | 'offboardingLetzterEinsatztag';

/** In the order they usually fall: the work at the school ends first. */
const STICHTAGE: { field: StichtagField; label: string }[] = [
  { field: 'offboardingLetzterEinsatztag', label: 'Letzter Tag im Einsatz' },
  { field: 'offboardingLetzterArbeitstag', label: 'Letzter Arbeitstag' },
];

export function OffboardingChecklist({ bewerber }: OffboardingChecklistProps) {
  const updateMutation = useUpdateBewerber();
  const [pendingField, setPendingField] = useState<OffboardingStep['field'] | null>(null);
  const [pendingStichtag, setPendingStichtag] = useState<StichtagField | null>(null);

  // Editable only while the wind-up is running. Afterwards the list is the record.
  const readOnly = bewerber.status !== BewerberStatus.OFFBOARDING;
  const doneCount = OFFBOARDING_STEPS.filter((step) => bewerber[step.field]).length;
  const total = OFFBOARDING_STEPS.length;

  const handleToggle = async (step: OffboardingStep, checked: boolean) => {
    setPendingField(step.field);
    try {
      await updateMutation.mutateAsync({
        id: bewerber.id,
        data: { [step.field]: checked },
      });
      toast.success(`„${step.label}" ${checked ? 'erledigt' : 'zurückgesetzt'}`);
    } catch (error) {
      console.error('Offboarding step update failed:', error);
      toast.error('Schritt konnte nicht gespeichert werden');
    } finally {
      setPendingField(null);
    }
  };

  // An empty field clears the date; the dashboard then counts the person again
  // as long as the other date has not passed.
  const handleStichtag = async (field: StichtagField, label: string, value: string) => {
    setPendingStichtag(field);
    try {
      await updateMutation.mutateAsync({ id: bewerber.id, data: { [field]: value || null } });
      toast.success(value ? `${label} gespeichert` : `${label} entfernt`);
    } catch (error) {
      console.error('Offboarding date update failed:', error);
      toast.error(`${label} konnte nicht gespeichert werden`);
    } finally {
      setPendingStichtag(null);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Offboarding{readOnly ? ' (abgeschlossen)' : ''}
        </h3>
        <span className="text-sm font-medium text-gray-500">
          {doneCount} / {total} erledigt
        </span>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <div className="mb-5 grid grid-cols-1 gap-4 border-b border-gray-200 pb-5 sm:grid-cols-2">
          {STICHTAGE.map(({ field, label }) => (
            <StichtagInput
              key={field}
              field={field}
              label={label}
              saved={bewerber[field]?.slice(0, 10) ?? ''}
              disabled={readOnly || pendingStichtag === field}
              onSave={(value) => handleStichtag(field, label, value)}
            />
          ))}
          <p className="text-xs text-gray-500 sm:col-span-2">
            Zählt als eingesetzter Schulbegleiter bis einschließlich zum früheren der beiden Tage.
            Ohne Datum zählt die Person weiter.
          </p>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {OFFBOARDING_STEPS.map((step) => {
            const checked = bewerber[step.field];
            const isPending = pendingField === step.field;
            return (
              <li key={step.field} className="flex items-start">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={readOnly || isPending}
                    onChange={(e) => handleToggle(step, e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{step.label}</span>
                </label>
                {step.description && <InfoIcon title={step.description} />}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default OffboardingChecklist;
